import { toISOStringYYYYMMDD } from '../../../../TaskScheduler/TaskSchedulerUtils'
import type { DialogTarget } from '../../../../TaskScheduler/TaskSettingsModal/DialogContext'
import TaskSettingsModal from '../../../../TaskScheduler/TaskSettingsModal/TaskSettingsModal'
import { GausiumExecutableTaskRecent, RobotApi } from 'api/RobotApi'
import { OdysseyRead, TaskTypeEnum } from 'api/schema'
import clsx from 'clsx'
import ActionsMenu, { ActionsMenuItem } from 'components/ActionsMenu/ActionsMenu'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import { ReactComponent as RemoveSvg } from 'images/icons/delete.svg'
import React from 'react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { selectRobot } from 'store/selectors/robot'
import { PATH } from 'utils/constants'
import getTestId from 'utils/getTestId'
import { odysseyStubFromLiveExecution, readLiveExecutionFromRobotRead } from 'utils/robotLiveExecution'
import { VENDOR } from 'utils/vendorDisplayName'

import style from './RobotTasks.module.css'

type CombinedTask = OdysseyRead & { isOdyssey?: boolean }
type GausiumRecentTask = GausiumExecutableTaskRecent & { isGausiumRecent: true }
type TaskRow = CombinedTask | GausiumRecentTask

interface RobotTasksProps {
  displayOffsetMinutes?: number
}

const RobotTasks = ({ displayOffsetMinutes }: RobotTasksProps) => {
  const robot = useSelector(selectRobot)
  const [combinedTasks, setCombinedTasks] = React.useState<CombinedTask[]>([])
  const { t } = useTranslation()
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [selectedTask, setSelectedTask] = React.useState<CombinedTask | null>(null)
  const [deleteTaskDialog, setDeleteTaskDialog] = useState(false)
  const [gausiumRecentTasks, setGausiumRecentTasks] = useState<GausiumRecentTask[]>([])
  const previousTasksKeyRef = useRef<string>('')
  const isGausium = robot.robotInfo?.vendor === VENDOR.GAUSIUM

  useEffect(() => {
    if (robot.robotInfo?.robotRaw) {
      // Create a key that includes both IDs and statuses to detect changes
      const odysseysKey =
        robot.robotInfo.robotRaw.odysseys
          ?.map(o => `${o.id}:${o.status || ''}`)
          .sort()
          .join(',') || ''
      const le = isGausium ? readLiveExecutionFromRobotRead(robot.robotInfo.robotRaw as any) : undefined
      const liveKey = le
        ? `le:${le.client_correlation_id}:${le.state || ''}:${le.task_name || ''}:${le.progress_percent ?? ''}:${
            le.map?.external_id || ''
          }`
        : ''
      const currentTasksKey = `${odysseysKey}|${liveKey}`

      // Skip update if data hasn't changed (including statuses)
      if (previousTasksKeyRef.current === currentTasksKey) {
        return
      }

      previousTasksKeyRef.current = currentTasksKey

      let odysseys = robot.robotInfo.robotRaw.odysseys?.map(o => ({ ...o, isOdyssey: true })) || []
      if (isGausium && le && !odysseys.some(o => o.id === le.client_correlation_id)) {
        odysseys = [
          ...odysseys,
          { ...odysseyStubFromLiveExecution(robot.robotInfo.robotRaw as any, le), isOdyssey: true },
        ]
      }
      const allTasks = [...odysseys]

      // Use Map for more reliable deduplication
      const tasksMap = new Map<string, CombinedTask>()
      allTasks.forEach(task => {
        const key = `odyssey_${task.id}`
        // If task already exists, take the newer one (with later updated_at or created_at)
        const existing = tasksMap.get(key)
        if (!existing) {
          tasksMap.set(key, task)
        } else {
          const existingDate = existing.updated_at || existing.created_at
          const currentDate = task.updated_at || task.created_at
          if (currentDate > existingDate) {
            tasksMap.set(key, task)
          }
        }
      })

      const uniqueTasks = Array.from(tasksMap.values())

      // Update state if task count, IDs, or statuses have changed
      setCombinedTasks(prevTasks => {
        const prevKey = prevTasks
          .map(t => `odyssey_${t.id}:${t.status || ''}`)
          .sort()
          .join(',')
        const newKey = uniqueTasks
          .map(t => `odyssey_${t.id}:${t.status || ''}`)
          .sort()
          .join(',')

        // If data hasn't changed (including statuses), keep previous state
        if (prevKey === newKey && prevTasks.length === uniqueTasks.length) {
          return prevTasks
        }

        // If new set contains all tasks from previous + new ones (superset),
        // use the new set completely (it's more complete and up-to-date)
        if (uniqueTasks.length >= prevTasks.length) {
          const prevTaskKeys = new Set(prevTasks.map(t => `odyssey_${t.id}`))
          const newTaskKeys = new Set(uniqueTasks.map(t => `odyssey_${t.id}`))
          const allPrevTasksInNew = Array.from(prevTaskKeys).every(key => newTaskKeys.has(key))

          if (allPrevTasksInNew) {
            return uniqueTasks
          }
        }

        // In other cases, use the new set
        return uniqueTasks
      })
    } else {
      if (previousTasksKeyRef.current !== '') {
        previousTasksKeyRef.current = ''
        setCombinedTasks([])
      }
    }
  }, [robot.robotInfo?.robotRaw, isGausium])

  useEffect(() => {
    if (!isGausium || !robot.robotInfo?.id) {
      setGausiumRecentTasks([])
      return
    }
    let cancelled = false
    RobotApi.fetchGausiumExecutableTasksRecent(robot.robotInfo.id)
      .then(rows => {
        if (cancelled) return
        setGausiumRecentTasks(rows.map(r => ({ ...r, isGausiumRecent: true as const })))
      })
      .catch(() => {
        if (!cancelled) setGausiumRecentTasks([])
      })
    return () => {
      cancelled = true
    }
  }, [isGausium, robot.robotInfo?.id, robot.robotInfo?.updatedAt])

  const getLoadingStatus = (task: CombinedTask) => {
    let loadingStatus = ''
    if (task.status) {
      loadingStatus =
        task.status === 'new' || task.status === 'scenario_error'
          ? 'Not loaded'
          : task.status === 'scenario_done'
          ? 'Loaded'
          : 'Loading'
    }
    return loadingStatus
  }

  const getStartDate = (task: CombinedTask) => {
    let startDate: Date
    if (task.scheduler_settings && task.scheduler_settings.datetime) {
      const isoStr = task.scheduler_settings.datetime.replace(' ', 'T')
      startDate = new Date(isoStr)
      const timeMatch = isoStr.match(/T(\d{2}):(\d{2})/)
      if (timeMatch) {
        startDate.setHours(parseInt(timeMatch[1], 10), parseInt(timeMatch[2], 10), 0, 0)
      }
    } else {
      startDate = new Date(task.created_at.replace(' ', 'T'))
      const validDateString = task.created_at.slice(0, 16) // Remove extra info
      startDate = new Date(validDateString)
    }
    return startDate
  }
  const getSchedule = (task: CombinedTask) => {
    let schedule = ''
    const days = task.scheduler_settings?.weekdays
    if (days) {
      if (days.SU === 'true') schedule += 'SU '
      if (days.MO === 'true') schedule += 'MO '
      if (days.TU === 'true') schedule += 'TU '
      if (days.WE === 'true') schedule += 'WE '
      if (days.TH === 'true') schedule += 'TH '
      if (days.FR === 'true') schedule += 'FR '
      if (days.SA === 'true') schedule += 'SA '
    }
    return schedule
  }

  const getAdditionalInfo = (task: CombinedTask) => {
    let days = ''
    if (task.scheduler_settings?.except_days) for (const day of task.scheduler_settings.except_days) days += day + ' '

    if (days === '') return ''

    return 'Except days: ' + days
  }

  const setSelectedTaskHandler = async (task: CombinedTask) => {
    if (robot.robotInfo && robot.robotInfo.robotRaw && task.job_type !== TaskTypeEnum.DELETE_SCENARIO) {
      setSelectedTask(task)
    } else {
      setSelectedTask(null)
    }
  }

  const handleTaskClick = (task: CombinedTask) => {
    if (!task.isOdyssey) {
      setSelectedTaskHandler(task)
      setIsModalOpen(true)
    }
  }

  const resetData = () => {
    setSelectedTask(null)
    setIsModalOpen(false)
  }
  const onCloseModal = () => {
    setIsModalOpen(true)
    resetData()
  }

  const onCancelClicked = () => {
    onCloseModal()
  }

  const onSaveClicked = async (refresh: boolean) => {
    onCloseModal()
    // Don't call fetchRobot here, as RobotView.tsx already updates data every 3 seconds
    // Periodic update in RobotView will pick up new data
  }

  const handleDeleteClicked = async (task: CombinedTask) => {
    await setSelectedTaskHandler(task)
    setDeleteTaskDialog(true)
  }

  const deleteTask = async () => {
    try {
      if (selectedTask) {
        if (selectedTask.isOdyssey) {
          await RobotApi.updateOdysseyType(selectedTask.id, TaskTypeEnum.DELETE_SCENARIO)
        }
        setDeleteTaskDialog(false)
        setSelectedTask(null)
      }
    } catch (error) {
      console.log('Error deleting tha task: ', error)
    }
  }

  const isNotEditableTask = (task: CombinedTask) => {
    const isToDelete = task.job_type === TaskTypeEnum.DELETE_SCENARIO
    const nowDate = new Date()
    const isPassed =
      !task.scheduler_settings?.weekdays &&
      toISOStringYYYYMMDD(
        task.scheduler_settings?.datetime ? new Date(task.scheduler_settings.datetime) : new Date(task.created_at)
      ) <= toISOStringYYYYMMDD(nowDate)
    return isToDelete || isPassed
  }

  const getTaskDate = (task: TaskRow) => {
    let dt: Date
    if ('isGausiumRecent' in task && task.isGausiumRecent) {
      const recent = task as GausiumRecentTask
      // command_created_at = time from GS robot command when matched; last_seen_at is often identical for all rows in one Celery sync
      const raw = recent.command_created_at || recent.created_at || recent.last_seen_at
      dt = new Date(String(raw || '').replace(' ', 'T') || 0)
    } else {
      dt = getStartDate(task as CombinedTask)
    }

    if (displayOffsetMinutes !== undefined) {
      const eventBrowserOffsetMinutes = -dt.getTimezoneOffset()
      const eventShiftMs = (displayOffsetMinutes - eventBrowserOffsetMinutes) * 60_000
      if (eventShiftMs !== 0) {
        return new Date(dt.getTime() + eventShiftMs)
      }
    }
    return dt
  }

  const getGausiumRecentTaskLabel = (task: GausiumRecentTask) => {
    const name = task.task_name?.trim()
    if (name) return name
    const map = task.map_name?.trim()
    if (map) return map
    return '—'
  }

  /** True only for GS executable-task mirror rows; always false for Autonomics mission/odyssey rows. */
  const isGausiumExecutableRow = (task: TaskRow): task is GausiumRecentTask =>
    'isGausiumRecent' in task && task.isGausiumRecent

  return (
    <div className={style.root}>
      <div className={style.tableWrapper}>
        <div className={style.tableHeader}>
          <div className={style.tableHeaderItem}></div>
          <div className={clsx(style.tableHeaderItem, style.centered)}>Task</div>
          <div className={clsx(style.tableHeaderItem, style.centered)}>
            Mission/
            <br />
            Scenario
          </div>
          <div className={style.tableHeaderItem}>Date</div>
          <div className={style.tableHeaderItem}>Schedule</div>
          <div className={clsx(style.tableHeaderItem, style.centered)}>Status</div>
          <div className={style.tableHeaderItem}></div>
        </div>
        <div className={style.table}>
          {[...(isGausium ? gausiumRecentTasks : combinedTasks)]
            .sort((a: TaskRow, b: TaskRow) => getTaskDate(b).getTime() - getTaskDate(a).getTime())
            .map(task => {
              const isLive = isGausiumExecutableRow(task)
              const combinedTask = task as CombinedTask
              return (
                <div key={isLive ? `gs_live_${task.id}` : `odyssey_${combinedTask.id}`} className={style.tableRow}>
                  <div
                    className={style.tableCol}
                    title={
                      isLive
                        ? ''
                        : combinedTask.job_type === TaskTypeEnum.DELETE_SCENARIO
                        ? 'The task will be deleted when the robot connects to the network'
                        : ''
                    }
                  >
                    {isLive ? (
                      ''
                    ) : combinedTask.job_type === TaskTypeEnum.DELETE_SCENARIO ? (
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <RemoveSvg />
                      </div>
                    ) : (
                      ''
                    )}
                  </div>
                  <div className={clsx(style.tableCol, style.centered)}>
                    {isLive ? getGausiumRecentTaskLabel(task) : combinedTask.id}
                  </div>
                  <div className={clsx(style.tableColWrap, style.centered)}>
                    {isLive ? (
                      task.map_name?.trim() || '—'
                    ) : (
                      <div className={style.missionLinksContainer}>
                        {(combinedTask as OdysseyRead).scenario ? (
                          <a
                            className={style.link}
                            href={`${PATH.SCENARIOS}${(combinedTask as OdysseyRead).scenario}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {(combinedTask as OdysseyRead).scenario}
                          </a>
                        ) : (
                          (combinedTask as OdysseyRead).missions.map((missionId, index) => (
                            <React.Fragment key={missionId}>
                              <a
                                className={style.link}
                                href={`${PATH.MISSION}${missionId}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {missionId}
                              </a>
                              {index < (task as OdysseyRead).missions.length - 1 && ', '}
                            </React.Fragment>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                  <div
                    className={style.tableColWrap}
                    title={
                      isLive
                        ? [
                            task.command_created_at && 'Command: ' + task.command_created_at,
                            task.created_at && 'First seen in FMS: ' + task.created_at,
                            task.last_seen_at && 'Last sync: ' + task.last_seen_at,
                          ]
                            .filter(Boolean)
                            .join('\n')
                        : undefined
                    }
                  >
                    {getTaskDate(task).toLocaleString([], {
                      year: 'numeric',
                      month: 'numeric',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false,
                    })}
                  </div>
                  <div className={style.tableColWrap} title={isLive ? '' : getAdditionalInfo(combinedTask)}>
                    {isLive ? '-' : getSchedule(combinedTask)}
                  </div>
                  <div className={clsx(style.tableCol, style.centered)}>
                    {isLive ? task.command_state || '-' : getLoadingStatus(combinedTask)}
                  </div>
                  <div className={style.tableCol} style={{ position: 'relative' }}>
                    <ActionsMenu>
                      <ActionsMenuItem
                        onClick={() => {
                          if (!isLive) handleTaskClick(combinedTask)
                        }}
                        data-test-id={getTestId('edit-zone-list')}
                        disabled={isLive || isNotEditableTask(combinedTask) || combinedTask.isOdyssey}
                      >
                        <span className={style.menuItemIcon} />
                        <span className={style.menuItemText} data-test-id={getTestId('edit-title')}>
                          {t('common.edit')}
                        </span>
                      </ActionsMenuItem>
                      <ActionsMenuItem
                        onClick={() => {
                          if (!isLive) handleDeleteClicked(combinedTask)
                        }}
                        data-test-id={getTestId('remove-zone-list')}
                        disabled={isLive || combinedTask.job_type === TaskTypeEnum.DELETE_SCENARIO}
                      >
                        <RemoveSvg className={style.menuItemIcon} />
                        <span data-test-id={getTestId('delete-title')}>{t('common.delete')}</span>
                      </ActionsMenuItem>
                    </ActionsMenu>
                  </div>
                </div>
              )
            })}
        </div>
        {(() => {
          const taskForEdit = selectedTask ? (selectedTask as unknown as OdysseyRead) : undefined
          const target: DialogTarget = taskForEdit ? { kind: 'task_all', taskId: taskForEdit.id } : { kind: 'new' }
          return (
            <TaskSettingsModal
              isOpen={isModalOpen}
              onClose={onCancelClicked}
              taskName={selectedTask?.name ? selectedTask.name : ''}
              target={target}
              preselectedData={taskForEdit ? { task: taskForEdit } : undefined}
              pickerScope={isGausium ? { vendor: VENDOR.GAUSIUM } : undefined}
              dateToEdit={null}
              onSubmit={onSaveClicked}
              displayOffsetMinutes={displayOffsetMinutes}
            />
          )
        })()}

        <ConfirmDialog
          title={selectedTask?.scheduler_settings?.weekdays ? 'Delete the scheduled task?' : 'Delete the task?'}
          isOpen={deleteTaskDialog}
          onClose={() => setDeleteTaskDialog(false)}
          onConfirmClick={deleteTask}
          confirmText={t('common.delete')}
          danger={true}
        />
      </div>
    </div>
  )
}

export default RobotTasks
